export const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_b214fb55-c9a7-4341-b982-cd6fc333f29f/artifacts/xkkrmpc3_MU_logo%204.png";

export const STORAGE_KEY = "mu_registration_v1"; // (unused — kept only to avoid stale imports)

// * denotes mandatory. Bonafide is "as applicable". Nationality proof is only
// mandatory when the candidate is a non-Indian (handled dynamically in code).
export const DOCUMENT_TYPES = [
  {
    id: "tenth",
    label: "10th Standard Certificate",
    description:
      "SSC Board / CBSE / HSC / ICSE / IB certificate, whichever is applicable. Max 1 MB.",
    multi: false,
    required: true,
  },
  {
    id: "twelfth",
    label: "12th Standard Certificate",
    description:
      "Intermediate Board / CBSE / HSC / ICSE / IB certificate, whichever is applicable. Max 1 MB.",
    multi: false,
    required: false,
    altGroup: "twelfth_group",
  },
  {
    id: "short_memo",
    label: "12th Short Memo (in lieu of 12th)",
    description:
      "Upload if the 12th standard certificate has not yet been issued. Max 1 MB.",
    multi: false,
    required: false,
    altGroup: "twelfth_group",
  },
  {
    id: "noc_twelfth",
    label: "NOC (in lieu of 12th & Short Memo)",
    description:
      "No Objection Certificate — upload only if neither the 12th certificate nor the Short Memo is available. Max 1 MB.",
    multi: false,
    required: false,
    altGroup: "twelfth_group",
  },
  {
    id: "tc",
    label: "Transfer Certificate",
    description: "TC from the last attended institution. Max 1 MB.",
    multi: false,
    required: false,
    altGroup: "tc_group",
  },
  {
    id: "noc_tc",
    label: "NOC (in lieu of Transfer Certificate)",
    description:
      "No Objection Certificate — upload only if TC is not available. Max 1 MB.",
    multi: false,
    required: false,
    altGroup: "tc_group",
  },
  {
    id: "migration",
    label: "Migration Certificate",
    description:
      "Migration certificate from previous Board / University, as applicable. Max 1 MB.",
    multi: false,
    required: false,
  },
  {
    id: "tuition_receipt",
    label: "Tuition Fee Receipt",
    description: "One or more tuition fee receipts. Max 1 MB per file.",
    multi: true,
    required: false,
  },
  {
    id: "hostel_receipt",
    label: "Hostel Fee Receipt",
    description:
      "One or more hostel fee receipts. Max 1 MB per file.",
    multi: true,
    required: false,
  },
  {
    id: "offer_letter",
    label: "Offer Letter",
    description: "Provisional offer letter issued by Mahindra University. Max 1 MB.",
    multi: false,
    required: true,
  },
  {
    id: "bonafide",
    label: "Bonafide / Conduct Certificate",
    description: "Conduct certificate from previous institution, as applicable.",
    multi: false,
    required: false, // as applicable
  },
  {
    id: "aadhaar",
    label: "Aadhaar Card",
    description: "Aadhaar copy of the candidate. Max 1 MB.",
    multi: false,
    required: true,
  },
  {
    id: "anti_ragging",
    label: "UGC Anti-Ragging Affidavit",
    description:
      "Signed affidavit (with reference no.) from antiragging.in. Max 1 MB. PDF only.",
    multi: false,
    required: true,
    accept: ".pdf",
  },
  {
    id: "nationality",
    label: "Nationality Proof",
    description:
      "Passport / Domicile / Nationality certificate. Mandatory for non-Indian candidates.",
    multi: false,
    required: false, // dynamic — required for non-Indians
  },
];

// Candidate fields an admin (roadmin) can flag for correction, using the same
// request/reason → candidate-fixes-it flow as document reupload. Parent names
// deliberately excluded — reupload requests are scoped to candidate name only.
export const CANDIDATE_FIELD_ITEMS = [
  { id: "name", label: "Candidate Name", type: "text" },
  { id: "photograph", label: "Photograph", type: "photo" },
];

export const STEPS = [
  { id: 1, key: "contact", label: "Contact & Parent Details" },
  { id: 2, key: "candidate", label: "Candidate Details" },
  { id: 3, key: "documents", label: "Document Upload" },
  { id: 4, key: "policies", label: "Forms & Policies" },
  { id: 5, key: "undertaking", label: "Undertaking" },
  { id: 6, key: "review", label: "Review & Submit" },
];

/** Steps shown in the wizard — omits Forms & Policies for employee/course registrants. */
export const getRegistrationSteps = (skipPolicies = false) =>
  skipPolicies ? STEPS.filter((s) => s.key !== "policies") : STEPS;

/** Map a stored step to a valid step when policies are skipped (e.g. resume on old step 4). */
export const normalizeStoredStep = (step, skipPolicies = false) => {
  const n = Number(step) || 1;
  if (skipPolicies && n === 4) return 5;
  return n;
};

export const registrationIdLabel = (skipPolicies = false) =>
  skipPolicies ? "Employee ID" : "Application No.";

// Signed-policy templates. Order matters — shown to the candidate in this
// exact sequence: (1) the official Anti-Drug Policy (view-only + "I have
// read and understood" checkbox, enabled only after opening the PDF), then
// (2) the Anti-Drug Annexure affidavit, then (3) High-Risk Consent. All
// three are mandatory.
export const POLICY_ITEMS = [
  {
    id: "antiDrugPolicy",
    label: "Anti-Drug Policy",
    description: "Read the official Mahindra University Anti-Drug Policy, then confirm you've understood it.",
    signers: "Candidate",
    // View-only: no fillable fields, no digital signature — just open the
    // real policy PDF and tick the acknowledgement checkbox once viewed.
    viewOnly: true,
    pdfUrl: "/policies/anti-drug-policy.pdf",
  },
  {
    id: "antiDrugAnnexure",
    label: "Anti-Drug Affidavit (Student + Parent)",
    description:
      "Fill the affidavit fields, preview it, then send for sign.",
    signers: "Candidate & Parent",
    interactive: true,
    // "son/daughter of Mr./Mrs." and the admission year are derived/fixed,
    // not user-editable — see PolicyBuilder's antiDrugAnnexure handling.
    fields: [
      { key: "studentName", label: "Student Name", type: "text", auto: "candidate.name", locked: true },
      { key: "parent1Name", label: "Parent / Guardian Name", type: "text", auto: "candidate.parent1Name", locked: true },
      { key: "address", label: "Address", type: "textarea", auto: "candidate.fullAddress", locked: true },
      { key: "phoneNumber", label: "Phone Number", type: "tel", auto: "mobile", locked: true },
      { key: "course", label: "Program", type: "text", auto: "courseInfo.program", locked: true, lockedHint: "From your admissions record." },
    ],
  },
  {
    id: "highRiskConsent",
    label: "High-Risk Consent",
    description:
      "Fill in your ward's details and any diagnosed medical illness (if applicable). Only Parent 1 needs to sign.",
    signers: "Parent 1",
    interactive: true,
    fields: [
      { key: "parent1Name", label: "Parent 1 Name", type: "text", auto: "candidate.parent1Name", locked: true },
      { key: "parent2Name", label: "Parent 2 Name", type: "text", auto: "candidate.parent2Name", locked: true },
      { key: "studentName", label: "Ward's Name", type: "text", auto: "candidate.name", locked: true },
      { key: "studentAge", label: "Age (in years)", type: "text", auto: "candidate.age", locked: true },
      { key: "studentGender", label: "Gender", type: "select", options: ["Male", "Female"], auto: "candidate.gender", locked: true },
      { key: "course", label: "Program", type: "text", auto: "courseInfo.program", locked: true, lockedHint: "From your admissions record." },
      { key: "medicalIllness", label: "Diagnosed Medical Illness (if any)", type: "text", placeholder: "Type None if not applicable" },
      { key: "parent1Phone", label: "Parent 1 Phone Number", type: "tel", auto: "candidate.parent1Phone", locked: true },
      { key: "parent2Phone", label: "Parent 2 Phone Number", type: "tel", auto: "candidate.parent2Phone", locked: true },
      { key: "declaredDate", label: "Declaration Date", type: "date", auto: "today", locked: true, lockedHint: "Today's date (India time)." },
    ],
  },
];

export const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-", "Not known to me"];
export const GENDERS = ["Male", "Female", "Other", "Prefer not to say"];

// Qualifying board for 12th standard — drives whether Transfer Certificate
// (or NOC in lieu) is mandatory (State Board & CBSE/ICSE) or optional.
export const EDUCATION_BOARDS = [
  { id: "state", label: "State Board (12th)" },
  { id: "cbse_icse", label: "CBSE / ICSE" },
  { id: "other", label: "IB / Cambridge / Any Other" },
];

// Boards for which Transfer Certificate (or NOC in lieu) is mandatory.
export const TC_MANDATORY_BOARDS = ["state", "cbse_icse"];

export const NATIONALITIES = ["Indian", "NRI", "OCI", "Foreign National"];

// UGC Anti-Ragging Affidavit — university info to be entered on the portal
export const UGC_UNIVERSITY_INFO = {
  affidavitUrl: "https://antiragging.in/affidavit_university_form.php",
  viceChancellor: "Dr Yajulu Medury",
  universityPhone: "040 67135160",
  courseType: "As per your admission (e.g. UG / Integrated UG)",
  courseName:
    "Full form of your program (e.g. Bachelor of Technology, Bachelor of Business Administration, Bachelor of Business Administration LLB, Bachelor of Arts LLB)",
  currentYear: "1",
  nearestPoliceStation: "Dundigal Police Station",
};
